import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { compose } from 'recompose'

import { connect } from 'react-redux'
import { notification, modal, logError } from 'redux/modules/app'
import {
  configuration as configurationApi,
  blobs as blobsApi
} from 'redux/modules/api'
import { CONFIGURATION_KEYS } from 'constants/enums'
import { updateSchema, updateSchemaBulk } from 'models/configurations'
import { getToken } from 'utils/token'


function hoc(config = {}) {
  return ComposedComponent => {
    const enhance = compose(
      connect(
        ({ api, auth }) => ({
          configuration: api.configuration,
          activeOrganization: auth.organization,
          user: auth.user
        }),
        { ...configurationApi, ...blobsApi, notification, modal, logError }
      )
    )

    class Configuration extends Component {
      static propTypes = {
        activeOrganization: PropTypes.object.isRequired,
        user: PropTypes.object,
        getConfigurationKeys: PropTypes.func.isRequired,
        getConfigurationKeyValue: PropTypes.func.isRequired,
        getConfigurationKeyValues: PropTypes.func.isRequired,
        decoOrganizationId: PropTypes.string,
        updateConfigurationKey: PropTypes.func.isRequired,
        updateConfigurationKeyBulk: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        modal: PropTypes.func.isRequired,
        uploadBlob: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        // configKey: PropTypes.string,
        // organizationId: PropTypes.number,
        getConfigurationAllValues: PropTypes.func.isRequired,
        configuration: PropTypes.object
      }

      static defaultProps = {
        params: {}
      }

      state = {
        isConfigUpdating: false
      }

      componentDidMount() {

        // const { configKey, organizationId, activeOrganization } = this.props
        const { user } = this.props

        if (config.onlyWithAuth && !getToken(user)) {
          return null
        }

        config.keys && this.handleGetConfigurationKeys()
        config.allValues && this.handleGetConfigurationAllValues()
        config.getValue && this.handleGetConfigurationValue(config.getValue)
        config.getValues && this.handleGetConfigurationValues(config.getValues)
      }

      handleGetOrganizationId = organizationId => {
        const { params, decoOrganizationId } = this.props

        let activeOrganizationId = organizationId

        if (
          config.inOrganizationDetail ||
          config.decoOrganizationId ||
          decoOrganizationId
        ) {
          activeOrganizationId =
            params.id || decoOrganizationId || activeOrganizationId
        }

        return activeOrganizationId
      }

      handleGetCurrentOrganisationId = () => {
        const { activeOrganization, params, decoOrganizationId } = this.props

        return params.id || decoOrganizationId || activeOrganization.id
      }

      handleGetConfigurationValues = (
        values,
        organizationId = this.props.activeOrganization.id
      ) => {
        const activeOrganizationId = this.handleGetOrganizationId(
          organizationId
        )

        if (!activeOrganizationId) {
          return
        }

        return this.props
          .getConfigurationKeyValues(values, {
            organizationId: activeOrganizationId
          })
          .catch(this.handleError)
      }


      handleGetConfigurationKeys = () =>
        this.props
          .getConfigurationKeys()
          .then(this.handleGetConfigurationKeyValue)
          .catch(this.handleError)

      handleGetConfigurationKeyValue = ({ body = [] }) => {
        body.forEach(configItem =>
          this.props.getConfigurationKeyValue(configItem.name)
        )
      }

      handleGetConfigurationValue = (
        value,
        organizationId = this.props.activeOrganization.id
      ) => {
        const activeOrganizationId = this.handleGetOrganizationId(
          organizationId
        )
        return this.props
          .getConfigurationKeyValue(value, {
            organizationId: activeOrganizationId
          })
          .catch(this.handleError)
      }

      handleGetConfigurationAllValues = keys => {
        const { params, activeOrganization } = this.props

        this.props.getConfigurationAllValues(
          params.id || activeOrganization.id,
          keys
        )
      }

      handleGetOrganizationParentalConsentConfiguration = organizationId => {
        if (!organizationId) {
          return
        }

        this.handleGetConfigurationValue(
          CONFIGURATION_KEYS
            .participantRegistrationParentalConsentElectronically.id,
          organizationId
        )
        this.handleGetConfigurationValue(
          CONFIGURATION_KEYS.participantRegistrationParentalConsentNotRequired
            .id,
          organizationId
        )
        this.handleGetConfigurationValue(
          CONFIGURATION_KEYS.participantRegistrationParentalConsentPaper.id,
          organizationId
        )
      }

      handleGetOrganizationRegistrationConfiguration = organizationId => {
        if (!organizationId) {
          return
        }

        this.handleGetConfigurationValue(
          CONFIGURATION_KEYS
            .enableEditPaymentNote.id,
          organizationId
        )
      }

      handleUpdateConfigurationValueBulk = async(fields, options = {}) => {
        let blobResponse = {}

        const orgId = this.handleGetCurrentOrganisationId()

        const hasMultipleValues =
          get(fields, 'configurationKey.maxNumberOfValues') > 1

        this.setState({ isConfigUpdating: true })

        if (fields.file) {
          try {
            blobResponse = await this.props.uploadBlob({
              file: fields.file
            })
          } catch (error) {
            this.handleCatch()
          }
        }

        let response
        try {
          const data = updateSchemaBulk(orgId, fields, options, get(blobResponse, 'body.url'))

          response = await this.props.updateConfigurationKeyBulk(data)

        } catch (error) {
          this.handleCatch(error)
        }

        const keys = Object.keys(get(options, 'configOptionsMap'))

        this.handleRefresh(keys, { hasMultipleValues })
        return response
      }


      handleUpdateConfigurationValue = async(fields, options = {}) => {
        const { configuration } = this.props

        let blobResponse = {}

        let sentWithId = false

        const currentOrganizationId = this.handleGetCurrentOrganisationId()

        const hasMultipleValues =
          get(fields, 'configurationKey.maxNumberOfValues') > 1

        if (currentOrganizationId === get(fields, 'organization.id')) {
          sentWithId = true
        }

        this.setState({
          isConfigUpdating: true
        })

        if (fields.file) {
          try {
            blobResponse = await this.props.uploadBlob({
              file: fields.file
            })
          } catch (error) {
            this.handleCatch()
          }
        }

        try {
          await this.props.updateConfigurationKey(
            options.key,
            currentOrganizationId,
            updateSchema(
              fields,
              { ...options, configuration },
              get(blobResponse, 'body.url'),
              sentWithId
            )
          )

          this.props.notification('Configuration value has been updated', {
            status: 'success',
            intlKey: 'notifications.configuration_value_has_been_updated'
          })
        } catch (error) {
          this.handleCatch(error)
        }

        this.handleRefresh([options.key], { hasMultipleValues })
      }

      handleResetConfigurationValue = async options => {
        const currentOrganizationId = this.handleGetCurrentOrganisationId()

        this.setState({
          isConfigUpdating: true
        })

        try {
          await this.props.updateConfigurationKey(
            options.key,
            currentOrganizationId,
            { array: [] }
          )
          this.props.notification('Configuration value has been reset', {
            status: 'success',
            intlKey: 'notifications.configuration_value_has_been_reset'
          })
        } catch (error) {
          this.handleCatch(error)
        }

        this.handleRefresh([options.key])
      }

      handleGetConfigurationManageParticipants = () => {
        this.props.getConfigurationKeyValue(
          CONFIGURATION_KEYS.manageAllParticipants.id
        )
      }

      isOrgConfigurationTrue = (orgId, configKey) => {
        const orgValuesMap = this.props.configuration && this.props.configuration.valuesByOrganizationId
        const values = orgValuesMap && orgValuesMap[orgId]
        const value = values && values[configKey]

        return value ? Boolean(value.booleanValue) : false
      }

      handleRefresh = (keys, { hasMultipleValues } = {}) => {
        this.setState({
          isConfigUpdating: false
        })

        this.props.modal(null)
        !hasMultipleValues && keys.forEach(k => this.handleGetConfigurationValue(k))

        config.allValues && this.handleGetConfigurationAllValues()
        config.getValues && this.handleGetConfigurationValues(config.getValues)
      }

      handleError = ({ body }) => {
        console.debug('error', body.userMessage)
        this.props.notification(body.userMessage, 'error')
      }

      handleCatch = response => {
        this.props.logError(response)

        this.setState({
          isConfigUpdating: false
        })
      }

      render() {
        return (
          <ComposedComponent
            {...this.props}
            getConfigurationManageParticipants={
              this.handleGetConfigurationManageParticipants
            }
            onGetConfigurationValues={this.handleGetConfigurationValues}
            onGetConfigurationValue={this.handleGetConfigurationValue}
            onGetOrganizationParentalConsentConfiguration={
              this.handleGetOrganizationParentalConsentConfiguration
            }
            onGetOrganizationRegistrationConfiguration={
              this.handleGetOrganizationRegistrationConfiguration
            }
            isOrgConfigurationTrue={this.isOrgConfigurationTrue}
            onUpdateConfigKey={this.handleUpdateConfigurationValue}
            onUpdateConfigKeyBulk={this.handleUpdateConfigurationValueBulk}
            onResetConfigKey={this.handleResetConfigurationValue}
            isConfigUpdating={this.state.isConfigUpdating}
          />
        )
      }
    }

    return enhance(Configuration)
  }
}

export default hoc
